import { SentMessageType } from "@sp-crm/core";
import {
    ConversationFeedBasicsFragment,
    GetUserMailboxMessagesForEntityQuery,
    SentMessageFragmentFragment,
} from "generated/graphql";

export type UnifiedMessageType = "sent" | "received" | "sms";

export interface UnifiedMessage {
    id: string;
    type: UnifiedMessageType;
    subject: string;
    timestamp: Date;
    fromDisplay: string;
    toDisplay: string;
    status?: string;
    latestEventStatus?: string | null;
    messageType?: SentMessageType | null;
    bodyPreview?: string;
    attachmentCount: number;
    originalSent?: SentMessageFragmentFragment;
    originalReceived?: GetUserMailboxMessagesForEntityQuery["getUserMailboxMessagesForEntity"][number];
    originalSmsThread?: ConversationFeedBasicsFragment["threads"][number]["smsThread"];
}

type ReceivedMessage =
    GetUserMailboxMessagesForEntityQuery["getUserMailboxMessagesForEntity"][number];

export function formatContactDisplay(
    name: string | null | undefined,
    email: string | null | undefined,
): string {
    if (name && email) {
        return `${name} <${email}>`;
    }
    return email || name || "";
}

export function convertReceivedMessage(message: ReceivedMessage): UnifiedMessage {
    const from = message.from;
    const fromDisplay = formatContactDisplay(from?.name, from?.email);

    const toDisplay = message.to
        .map(r => formatContactDisplay(r.contact.name, r.contact.email))
        .join(", ");

    return {
        id: message.id,
        type: "received",
        subject: message.subject,
        timestamp: new Date(message.receivedTimestamp),
        fromDisplay,
        toDisplay,
        bodyPreview: message.bodyPreview,
        attachmentCount: 0,
        originalReceived: message,
    };
}

export function convertSentMessage(message: SentMessageFragmentFragment): UnifiedMessage {
    const toDisplay = (message.to ?? []).join(", ");

    return {
        id: message.id,
        type: "sent",
        subject: message.subject ?? "(no subject)",
        timestamp: new Date(message.createdAt),
        fromDisplay: "You",
        toDisplay,
        status: message.status,
        latestEventStatus: message.latestEventStatus,
        messageType: message.messageType as SentMessageType | null,
        attachmentCount: message.attachments?.length ?? 0,
        originalSent: message,
    };
}

export function convertSmsThread(
    thread: ConversationFeedBasicsFragment["threads"][number],
): UnifiedMessage | null {
    if (!thread.smsThread) return null;

    const latestMessage = thread.smsThread.messages[0];
    if (!latestMessage) return null;

    const isOutbound = latestMessage.direction === "Outbound";

    return {
        id: thread.id,
        type: "sms",
        subject: thread.smsThread.messages[0]?.body ?? "(no content)",
        timestamp: new Date(thread.latestTimestamp),
        fromDisplay: isOutbound ? "You" : thread.smsThread.contact.phoneNumber,
        toDisplay: isOutbound ? thread.smsThread.contact.phoneNumber : "You",
        bodyPreview: latestMessage.body,
        attachmentCount: 0,
        originalSmsThread: thread.smsThread,
    };
}

export function mergeAndSortMessages(
    receivedMessages: ReceivedMessage[],
    conversationFeed: ConversationFeedBasicsFragment | null | undefined,
): UnifiedMessage[] {
    const unified: UnifiedMessage[] = [];

    for (const msg of receivedMessages) {
        unified.push(convertReceivedMessage(msg));
    }

    if (conversationFeed?.threads) {
        for (const thread of conversationFeed.threads) {
            if (thread.type === "Email" && thread.sentMessage) {
                unified.push(convertSentMessage(thread.sentMessage));
            } else if (thread.type === "Sms" && thread.smsThread) {
                const smsMessage = convertSmsThread(thread);
                if (smsMessage) {
                    unified.push(smsMessage);
                }
            }
        }
    }

    unified.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return unified;
}
